<template>
  <div class="play-multiplayer w-[100lvw] h-[100lvh] px-20 pt-20 bg-cw-green flex justify-center items-center">
    <div class="fixed top-[30px] left-[30px]">
      <BaseButton background-color="var(--color-cw-olive)" border-color="#323617" class="w-10 md:w-11 lg:w-12 h-10 md:h-9 lg:h-10 justify-center" @click="leaveGame">
        <ArrowLeftIcon />
      </BaseButton>
    </div>
    <div class="grid grid-cols-[1fr_max-content] justify-between items-start max-w-7xl gap-x-48 lg:gap-x-60 xl:scale-125">
      <div class="flex max-w-[300px]">
        <div class="flex flex-col gap-y-4">
          <div class="flex flex-col gap-y-1 md:gap-y-2 lg:gap-y-3">
            <p class="text-lg md:text-2xl lg:text-3xl font-bold">Invite friends</p>
            <p class="text-xs lg:text-sm font-medium">Ask your friends to join a game with this code</p>
          </div>
          <button class="w-max p-2.5 rounded-[10px] flex gap-x-1 items-baseline bg-cw-dark-alt/70" @click="copyCode">
            <p class="text-xl leading-4 font-bold">{{ gameState?.code }}</p>
            <CopyIcon />
          </button>
          <button v-if="isTurn" class="grid grid-cols-[max-content_max-content] gap-x-1.5 items-center" @click="shareCode">
            <p class="text-sm font-medium">Share</p>
            <ShareIcon />
          </button>
        </div>
      </div>
      <div class="flex flex-col gap-y-14 md:gap-y-16 lg:gap-y-20 justify-center items-end">
        <div class="max-w-[350px]">
          <div class="grid grid-cols-4 gap-x-6 gap-y-4 lg:gap-y-5">
            <div v-for="opponent in opponents" :key="opponent.id" class="flex flex-col gap-y-1.5 items-center">
              <BaseAvatar :size="60" :src="opponent.avatarUrl" />
              <div class="flex items-center gap-x-[0.5px]">
                <p class="text-[10px] font-bold">{{ opponent.username || 'Matching...' }}</p>
                <span v-if="opponent.username === 'Waiting'" class="dots text-[10px] font-bold tracking-normal" />
              </div>
            </div>
          </div>
          <div v-if="isWaitingTurn" class="p-4 rounded-md bg-cw-green-alt mt-6">
            <p class="text-xs font-medium text-[#A3C2B6]">
              <span class="text-white">{{ currentTurnPlayerProfile?.username }}</span>
              will start the game when you are all set.
            </p>
          </div>
        </div>

        <BaseButton
          v-if="isTurn"
          background-color="#1AA570"
          border-color="var(--color-cw-green-border-shadow)"
          class="md:px-4 md:py-2.5 lg:px-6 lg:py-3.5"
          :disabled="allPlayers!.length < MIN_PLAYERS"
          @click="startGame"
        >
          <p class="w-full !text-xs xl:!text-sm">Start game</p>
        </BaseButton>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, inject } from 'vue'
import { useClipboard } from '@vueuse/core'
import { useGameStore } from '@/stores/game'
import { GameEvent } from '@/types/Events'
import useSocket from '@/composables/useSocket'
import BaseAvatar from '@/components/base/BaseAvatar.vue'
import BaseButton from '@/components/base/BaseButton.vue'
import CopyIcon from '@/assets/svgs/icons/shared-icons-outline-bold-copy.svg'
import ShareIcon from '@/assets/svgs/icons/share-fat.svg'
import ArrowLeftIcon from '@/assets/svgs/icons/arrow-fat-left.svg'
import { storeToRefs } from 'pinia'
import { useHelpers } from '@/composables/useHelpers'
import { useUserStore } from '@/stores/user'

const { VITE_APP_ENV: environs } = import.meta.env

const showLoader = inject('showLoader') as InjectedFunctionT
const hideLoader = inject('hideLoader') as InjectedFunctionT

const socket = useSocket()
const { showToast } = useHelpers()
const { resetGame } = useGameStore()
const { isKiddiesAccount } = storeToRefs(useUserStore())
const { copy } = useClipboard()
const { createDummyPlayer, handleCustomError } = useHelpers()

const { gameState, allPlayers, currentTurnPlayerProfile, MIN_PLAYERS, MAX_PLAYERS, isTurn, isWaitingTurn } = storeToRefs(useGameStore())
const opponents = computed(() => {
  const players = allPlayers.value || []
  const currentPlayerCount = players.length

  const dummyPlayersNeeded = MAX_PLAYERS.value - currentPlayerCount

  const dummyPlayers = Array.from({ length: dummyPlayersNeeded }, (_, index) =>
    createDummyPlayer({
      id: `dummy-${index}`,
      username: `Waiting`,
      firstName: `Waiting`,
      lastName: ``,
      avatarUrl: '',
      points: 0,
      isLetoUser: false,
      status: 'online',
      hand: [],
      type: 'human',
      actionTimeout: -1,
      reconnectTimeout: -1,
      school: null,
      email: null
    })
  )

  return [...players, ...dummyPlayers]
})

const startGame = async () => {
  try {
    showLoader()
    await socket.sendEventAsync(GameEvent.StartGame)
  } catch (error) {
    handleCustomError(error)
  } finally {
    hideLoader()
  }
}

const leaveGame = async () => {
  try {
    showLoader()
    await socket.sendEventAsync(GameEvent.LeaveGame)
    resetGame()
  } catch (error) {
    handleCustomError(error)
  } finally {
    hideLoader()
  }
}

const copyCode = async () => {
  copy(gameState.value?.code || '')
  showToast('Copied')
}

const shareCode = async () => {
  let url = ''
  if (isKiddiesAccount.value) {
    url = environs === 'staging' ? 'https://kids-staging.cowrywise.com' : 'https://kids.cowrywise.com'
    return
  } else {
    url = environs === 'staging' ? 'https://psi-staging.games.cowrywise.com' : 'https://psi.games.cowrywise.com'
  }
  const text = `Think you can beat me at a game of cards?🃏 Well game on! 😎 Sign up at ${url} and use the code, ${gameState.value?.code}, to join my game room. Don't keep me waiting. Let's see what you got 🫵🏽`

  if (navigator.share) {
    try {
      await navigator.share({
        title: 'Join my PSI lobby!',
        text: `Think you can beat me at a game of cards?🃏 Well game on! 😎 Use the code ${gameState.value?.code} to join my game room. Don't keep me waiting. Let's see what you got 🫵🏽`,
        url
      })
    } catch (error) {
      if ((error as Error).name !== 'AbortError') {
        copy(text)
        showToast('Copied to clipboard')
      }
    }
  } else {
    copy(text)
    showToast('Copied to clipboard')
  }
}
</script>

<style scoped>
.play-multiplayer {
  background-color: var(--color-cw-green);
  background-image: url('@/assets/images/noise-texture.webp');
  background-size: cover;
  background-position: center;
  background-repeat: no-repeat;
}
</style>
